import React, { useState, useRef } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../service/IdApi";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { Column } from "primereact/column";
import { Button } from 'primereact/button';
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import DataUtils from "../../../utils/DataUtils";
import { useLocation } from "react-router-dom";
import { format } from 'date-fns';

const UserEventLog = () => {
    const location = useLocation();
    const [events, setEvents] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [eventTableState, setEventTableState] = useState(() => {
        return {
            first: 0,
            rows: 25,
            page: 1,
            sortField: "map.properties.map.$mp_api_timestamp_ms",
            sortOrder: -1
        };
    });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [searchEmail, setSearchEmail] = useState("");
    const [validSearch, setValidSearch] = useState(false);

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["UserEvents"], async () => {
        var body = {};
        body.first = eventTableState.first;
        body.rows = eventTableState.rows;
        body.sortField = eventTableState.sortField;
        body.sortOrder = eventTableState.sortOrder;
        body.email = searchEmail;
        if (!DataUtils.isEmpty(searchEmail)) {
            const result = await authApi.post("admin/user-events", body);
            setEvents(result.data.events.myArrayList);
            setTotalRecords(result.data.totalRecords);
            setValidSearch(true);
        }
        else {
            const searchParams = new URLSearchParams(location.search);
            const emailParam = searchParams.get("email");
            if (emailParam) {
                body.email = emailParam
                setSearchEmail(emailParam);
                const result = await authApi.post("admin/user-events", body);
                setEvents(result.data.events.myArrayList);
                setTotalRecords(result.data.totalRecords);
                setValidSearch(true);
            }
            else {
                setTotalRecords(0);
                setValidSearch(false);
            }
        }
        setLoaded(true);
        return data;
    });

    const pageHeader = (
        <PageHeading pageTitle="Admin - User Event Log">
            <div className="block">
            </div>
        </PageHeading>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const onPage = (event) => {
        setEventTableState(event);
    };

    const onSort = (event) => {
        setEventTableState(event);
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const rowClicked = (e, rowData) => {
        e.preventDefault();
        setSelectedEvent(rowData);
        setShowDetailsDialog(true);
    }

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const eventBodyTemplate = (rowData) => {
        return (
            <span>
                <a href="#" onClick={(e) => rowClicked(e, rowData)}>
                    {rowData.map.event}
                </a>
            </span>
        );
    }

    const handleSearch = () => {
        refetch();
    };

    const formatDate = (timestamp) => {
        const date = new Date(parseInt(timestamp));
        return format(date, 'MMM d, yyyy hh:mm:ss a');
    };

    const dateBodyTemplate = (rowData) => {
        return formatDate(rowData.map.properties.map.$mp_api_timestamp_ms);
    };

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex pt-3 w-full">
                        <div className="flex flex-row text-lg align-items-center">
                            Enter an email address and search Mixpanel for events in the past 30 days.
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                        </div>
                    </div>
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row">
                            <div className="p-inputgroup mr-3">
                                <InputText
                                    placeholder="Search by email"
                                    value={searchEmail}
                                    onChange={(e) => setSearchEmail(e.target.value)}
                                />
                                <Button
                                    icon="pi pi-search"
                                    className="p-button-primary"
                                    onClick={handleSearch}
                                    tooltip="Search"
                                    loading={isLoading || isFetching}
                                />
                            </div>
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Button type="button" icon="pi pi-file-export" rounded onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    {validSearch && (
                        <DataTable
                            ref={dataTable}
                            value={events}
                            dataKey="map.properties.map.$insert_id"
                            paginator
                            rows={eventTableState.rows}
                            rowsPerPageOptions={[25, 50, 100]}
                            className="datatable-responsive"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} user events"
                            emptyMessage="No user events found."
                            first={eventTableState.first}
                            totalRecords={totalRecords}
                            onPage={onPage}
                            onSort={onSort}
                            sortField={eventTableState.sortField}
                            sortOrder={eventTableState.sortOrder}
                            loading={isLoading || isFetching || !loaded}
                        >
                            <Column field="map.event" body={eventBodyTemplate} header="Event Type" />
                            <Column body={dateBodyTemplate} field="map.properties.map.$mp_api_timestamp_ms" header="Created On" sortable />
                        </DataTable>
                    )}
                </div>
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="MixPanel Event Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedEvent && (
                        <div>
                            <div className="field grid mb-0">
                                <div className="col">
                                    <div className="mb-2 block">
                                        <strong>Event:</strong> {selectedEvent.map.event}
                                    </div>
                                    {Object.entries(selectedEvent.map.properties.map).map(([key, value]) => (
                                        <div key={key} className="mb-2 block">
                                            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                    )}
                </Dialog>
            </div>
        </div>
    );
};

export default UserEventLog;
